import { useMemo } from 'react';

import {
    AddIcon,
    AddKitIcon,
    BodySmall,
    Box,
    Button,
    CallMergeOutlined,
    CloseIcon,
    ControlPointDuplicateIcon,
    CreateSectionIcon,
    DeleteOutlineIcon,
    EditOutlined,
    EditIcon,
    HighlightOffIcon,
    HighlightOutlinedIcon,
    IconButton,
    MoveDownIcon,
    SearchIcon,
    TextField,
    Tooltip,
    VisibilityIcon,
    VisibilityOffIcon
} from '@parspec/pixel';

export type ToolBarT = 'delete' | 'search' | 'clearFilters' | 'hide' | 'unhide' | 'selectedItems' | 'duplicate' | 'add' | 'createKit' | 'move' | 'highlight' | 'edit' | 'merge' | 'createSection' | 'bulkEdit';

interface ICustomToolBarPanel {
    toolBarOptions: ToolBarT[];
    onTextSearch?: (searchString: string) => void;
    onAdd?: () => void;
    onAddDuplicates?: () => void;
    onEdit?: () => void;
    onMerge?: () => void;
    onDelete?: () => void;
    onHideUnhide?: () => void;
    onCreateKit?: () => void;
    onMove?: () => void;
    onCloseBanner?: () => void;
    onClearFilter?: () => void;
    onHighlight?: () => void;
    onCreateSection?: () => void;
    onBulkEdit?: () => void;
    selectedRowCount: number;
    disabledToolBarButton: boolean;
    title?: string;
    loading?: boolean;
    toolbarRightSection?: React.ReactNode;
    isKitSelected?: boolean;
    searchText?: string;
    gridRef?: any;
    isFilterApplied?: boolean;
    isCreateKitDisabled?: boolean;
    isChangeSectionDisabled?: boolean;
    isDuplicateDisabled?: boolean;
    selectedText?: string;
    isHighlightDisabled?: boolean;
    showBorder?: boolean;
    bulkActionsHideUnhideStatus?: "hide" | "un-hide";
    bulkActionHighLightStatus?: 'highlight' | 'unhighlight';
}
export const CustomToolBarPanel = (props: ICustomToolBarPanel) => {
    const {
        toolBarOptions,
        onTextSearch,
        onAdd,
        onAddDuplicates,
        onEdit,
        onMerge,
        onDelete,
        onHideUnhide,
        onCreateKit,
        onMove,
        onClearFilter,
        selectedRowCount,
        disabledToolBarButton,
        onCloseBanner,
        title,
        loading,
        toolbarRightSection,
        onHighlight,
        searchText,
        isFilterApplied,
        onCreateSection,
        onBulkEdit,
        isCreateKitDisabled,
        isChangeSectionDisabled,
        selectedText,
        isHighlightDisabled,
        showBorder,
        bulkActionsHideUnhideStatus = "un-hide",
        bulkActionHighLightStatus = "highlight"
    } = props;

    const handleOnClearFilter = () => {
        onClearFilter && onClearFilter();
        onTextSearch?.('');
    };

    return (
        <Box display={'flex'} justifyContent="space-between" alignItems={'flex-end'} mb={2} sx={loading ? { PointerEvent: 'none' } : {}}>
            <Box width={'100%'} height={'100%'} display="flex" alignItems="center" gap={2}>
                {title && <BodySmall color="text.primary">{title}</BodySmall>}

                {/* For filtering of table */}
                {toolBarOptions?.includes('search') && (
                    <Box width={300}>
                        <TextField
                            label=""
                            placeholder="Search..."
                            startIcon={<SearchIcon fontSize="small" />}
                            size="small"
                            value={searchText}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onTextSearch?.(event?.target?.value?.toLowerCase())}
                        />{' '}
                    </Box>
                )}

                <Box display='flex' alignItems='center' gap={1} sx={showBorder ? { border: '1px solid', borderColor: 'neutral.main', borderRadius: '6px' } : {}}>

                    {/* for clearing applied filters */}
                    {toolBarOptions?.includes('clearFilters') && (isFilterApplied || searchText) && (
                        <Tooltip title="Clear Filter(s)">
                            <Button onClick={handleOnClearFilter} disabled={loading} variant="text" color="primary">
                                Clear Filters
                            </Button>
                        </Tooltip>
                    )}

                    {/* For addition of row */}
                    {toolBarOptions?.includes('add') && (
                        <Tooltip title={'Add'}>
                            <Box>
                                <IconButton
                                    disabled={loading}
                                    onClick={() => {
                                        if (onAdd) onAdd();
                                    }}
                                >
                                    <AddIcon fontSize="medium" />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For creating Section */}
                    {toolBarOptions?.includes('createSection') && (
                        <Tooltip title={'Create Section'}>
                            <Box data-testid="create-section-btn">
                                <IconButton onClick={() => onCreateSection?.()} disabled={loading}>
                                    <CreateSectionIcon fontSize="small" color='secondary' />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For creating kit */}
                    {toolBarOptions?.includes('createKit') && (
                        <Tooltip title={isCreateKitDisabled ? "Line items should be selected from a single section and no kit/component line should be selected." : 'Create Kit'}>
                            <Box data-testid="create-kit-btn">
                                <IconButton onClick={() => onCreateKit!()} disabled={loading || isCreateKitDisabled} sx={(loading || isCreateKitDisabled) ? { opacity: 0.3 } : {}}>
                                    <AddKitIcon fontSize="small" color='secondary' />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For bulk edit */}
                    {toolBarOptions?.includes('bulkEdit') && (
                        <Tooltip title={disabledToolBarButton ? 'Select Item(s) First' : 'Bulk Edit'}>
                            <Box data-testid="bulk-edit-btn">
                                <IconButton onClick={() => onBulkEdit?.()} disabled={disabledToolBarButton || loading} sx={(disabledToolBarButton || loading) ? { opacity: 0.3 } : {}}>
                                    <EditIcon fontSize="small" color='secondary' />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For changing the section */}
                    {toolBarOptions?.includes('move') && (
                        <Tooltip title={disabledToolBarButton ? 'Select Item(s) First' : isChangeSectionDisabled ? "Line items should be selected from a single section and at least one product/kit should be selected." : 'Change Section'}>
                            <Box data-testid="move-btn">
                                <IconButton onClick={() => onMove!()} disabled={disabledToolBarButton || loading || isChangeSectionDisabled}>
                                    <MoveDownIcon fontSize="medium" />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For Heighlight Row*/}
                    {toolBarOptions?.includes('highlight') && (
                        <Tooltip
                            title={disabledToolBarButton ? 'Select Item(s) First' : isHighlightDisabled ? "Atleast one product should be selected." : bulkActionHighLightStatus === "highlight" ? "Highlight" : 'Unhighlight'}
                        >
                            <Box>
                                <IconButton
                                    disabled={disabledToolBarButton || loading || isHighlightDisabled}
                                    onClick={() => {
                                        if (onHighlight) onHighlight();
                                    }}
                                >
                                    {bulkActionHighLightStatus === "highlight" ? <HighlightOutlinedIcon fontSize="medium" /> : <HighlightOffIcon fontSize="medium" />}
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For hiding and unhiding rows */}
                    {toolBarOptions?.includes('hide') && (
                        <Tooltip title={disabledToolBarButton ? 'Select Item(s) First' : bulkActionsHideUnhideStatus === "hide" ? "Hide" : "Unhide"}>
                            <Box>
                                <IconButton
                                    onClick={() => {
                                        if (onHideUnhide) onHideUnhide();
                                    }}
                                    disabled={disabledToolBarButton || loading}
                                >
                                    {bulkActionsHideUnhideStatus === "hide" ? <VisibilityOffIcon fontSize="medium" /> : <VisibilityIcon fontSize="medium" />}
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For duplicating selected row */}
                    {toolBarOptions?.includes('duplicate') && (
                        <Tooltip title={disabledToolBarButton ? 'Select Item(s) First' : 'Duplicate'}>
                            <Box>
                                <IconButton
                                    onClick={() => {
                                        if (onAddDuplicates) onAddDuplicates();
                                    }}
                                    disabled={disabledToolBarButton || loading}
                                >
                                    <ControlPointDuplicateIcon fontSize="medium" />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For bulk editing rows with outlined icon */}
                    {toolBarOptions?.includes('edit') && (
                        <Tooltip title={disabledToolBarButton ? 'Select Item(s) First' : 'Edit'}>
                            <Box>
                                <IconButton
                                    disabled={disabledToolBarButton || loading}
                                    onClick={onEdit}
                                >
                                    <EditOutlined fontSize="medium" />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For merging exactly 2 rows */}
                    {toolBarOptions?.includes('merge') && (
                        <Tooltip title={selectedRowCount !== 2 ? 'Select 2 items to merge' : 'Merge'}>
                            <Box>
                                <IconButton
                                    disabled={selectedRowCount !== 2 || loading}
                                    onClick={onMerge}
                                >
                                    <CallMergeOutlined fontSize="medium" />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                    {/* For deleting row */}
                    {toolBarOptions?.includes('delete') && (
                        <Tooltip title={disabledToolBarButton ? 'Select Item(s) First' : 'Delete'}>
                            <Box>
                                <IconButton
                                    disabled={disabledToolBarButton || loading}
                                    onClick={() => {
                                        if (onDelete) onDelete();
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="medium" />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )}

                </Box>

                {/* For showing banner of, number of rows selected */}
                {toolBarOptions?.includes('selectedItems') && selectedRowCount > 0 && <SelectedItemsCount count={selectedRowCount} closeBanner={onCloseBanner!} selectedText={selectedText} />}
            </Box>
            <Box width="100%" display="flex" justifyContent="flex-end">
                {toolbarRightSection}
            </Box>
        </Box>
    );
};

export default CustomToolBarPanel;

interface SelectedItemsCountProps {
    count?: number;
    closeBanner: () => void;
    selectedText?: string;
}
export const SelectedItemsCount = (props: SelectedItemsCountProps) => {
    const { count, closeBanner, selectedText } = props;


    const content = useMemo(() => {
        if (selectedText) return selectedText;
        return `${count || 0} item(s) selected`
    }, [count, selectedText])

    return (
        <Box p={1} pl={3} pr={2} width='max-content' bgcolor={'primary.main'} color={'secondary.contrastText'} display="flex" alignItems="center" gap={2}>
            <BodySmall color="secondary.contrastText" limit={false}>
                {content}
            </BodySmall>
            <IconButton onClick={closeBanner} sx={{ color: 'secondary.contrastText', margin: 0, padding: 0 }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};
